import React from "react";
import { isInAppBrowser } from "../lib/safeStorage";

/**
 * Root ErrorBoundary — catches any render/lifecycle crash so users never see
 * a raw black screen. Detects Instagram/Facebook in-app browsers and shows a
 * specific "Open in Safari/Chrome" prompt because those WebViews cause the
 * majority of black-screen reports (blocked storage, backdrop-filter fails,
 * stale service workers, etc.).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Best-effort log to console for debugging
    if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.error("[Nexus] Uncaught render error:", error, info);
    }
  }

  handleReload = () => {
    try {
      // Unregister any service worker (stale SW is the second most common cause)
      if (typeof navigator !== "undefined" && navigator.serviceWorker) {
        navigator.serviceWorker.getRegistrations?.().then((regs) => {
          regs.forEach((r) => r.unregister());
        });
      }
    } catch {
      // ignore
    }
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const inApp = isInAppBrowser();
    const url = typeof window !== "undefined" ? window.location.href : "https://collabnexus.co.uk";

    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#060608",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          fontFamily: "system-ui, -apple-system, Segoe UI, sans-serif",
          textAlign: "center",
        }}
        data-testid="root-error-boundary"
      >
        <div style={{ maxWidth: 480 }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 16,
              margin: "0 auto 20px",
              background: "linear-gradient(135deg, #FF107A, #9D4EDD, #00D4FF)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
              fontWeight: 800,
            }}
          >
            N
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 10 }}>
            Nexus couldn't load here
          </h1>

          {inApp ? (
            <>
              <p style={{ color: "#a1a1aa", fontSize: 15, lineHeight: 1.55, marginBottom: 20 }}>
                You're browsing inside Instagram's built-in browser, which
                sometimes blocks the storage and animations Nexus needs.
                Tap the <b>•••</b> menu and choose <b>"Open in browser"</b>
                {" "}(Safari or Chrome) — the site will load correctly there.
              </p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  padding: "12px 22px",
                  borderRadius: 999,
                  background: "linear-gradient(90deg, #FF107A, #9D4EDD, #00D4FF)",
                  color: "#fff",
                  fontWeight: 700,
                  textDecoration: "none",
                  fontSize: 14,
                }}
                data-testid="open-in-browser-cta"
              >
                Open Nexus in your browser
              </a>
            </>
          ) : (
            <>
              <p style={{ color: "#a1a1aa", fontSize: 15, lineHeight: 1.55, marginBottom: 20 }}>
                Something went wrong loading the page. This is usually fixed by
                a hard refresh — we've also cleared the cached copy for you.
              </p>
              <button
                onClick={this.handleReload}
                style={{
                  display: "inline-block",
                  padding: "12px 22px",
                  borderRadius: 999,
                  background: "linear-gradient(90deg, #FF107A, #9D4EDD, #00D4FF)",
                  color: "#fff",
                  fontWeight: 700,
                  border: "none",
                  cursor: "pointer",
                  fontSize: 14,
                }}
                data-testid="reload-cta"
              >
                Refresh Nexus
              </button>
            </>
          )}

          <p style={{ marginTop: 22, fontSize: 12, color: "#71717a" }}>
            Still stuck? Email us at{" "}
            <a href="mailto:info@collabnexus.co.uk" style={{ color: "#a1a1aa" }}>
              info@collabnexus.co.uk
            </a>
          </p>
        </div>
      </div>
    );
  }
}
